/**
 * @workspace/access-control
 *
 * Pure (framework-free) access-control rules shared by the web app
 * (`artifacts/bookaclassic`) and the mobile app (`artifacts/mobile`, when it
 * lands). Both surfaces import from this module so that mode/role behaviour
 * — host eligibility, the guest↔host toggle, route classification, the
 * persistence key — cannot drift between platforms.
 *
 * IMPORTANT: nothing in here may import React, the DOM, AsyncStorage, or any
 * platform-specific API. Each consumer wraps these helpers in a thin
 * platform-appropriate adapter (React context for the web, a Provider over
 * AsyncStorage for the mobile app).
 */

/** The two "hats" a signed-in user can wear in the product. */
export type AppMode = "guest" | "host";

/** Minimal user-profile shape used for host-eligibility checks. */
export interface HostEligibilityProfile {
  /** True when the profile row has `is_host = true`. */
  is_host?: boolean | null;
}

/**
 * Returns true when the signed-in profile may switch into the host hat.
 *
 * This is the canonical predicate — both the AppMode toggle and the
 * become-a-host CTA gate on the result. A non-host's UI must never expose
 * the host toggle, and any persisted `mode === "host"` for a non-host is
 * coerced back to `"guest"` before being applied.
 */
export function canEnterHostMode(profile: HostEligibilityProfile | null | undefined): boolean {
  return !!profile?.is_host;
}

/**
 * Reconciles the next requested mode with the user's current host
 * eligibility. Non-hosts are pinned to `"guest"` regardless of input.
 */
export function reconcileMode(
  next: AppMode,
  profile: HostEligibilityProfile | null | undefined,
): AppMode {
  return canEnterHostMode(profile) ? next : "guest";
}

/** Storage-key namespace for persisted per-user mode. */
export const APP_MODE_STORAGE_PREFIX = "bac:appMode:";

/**
 * Build the per-user storage key for the persisted mode. Returns `null` for
 * anonymous users (no signed-in user → nothing to remember).
 */
export function appModeStorageKey(userId: string | null | undefined): string | null {
  if (!userId) return null;
  return APP_MODE_STORAGE_PREFIX + userId;
}

/**
 * Coerce an arbitrary stored value back into a valid `AppMode`. Anything
 * other than the literal string `"host"` becomes `"guest"`.
 */
export function parseStoredMode(raw: unknown): AppMode {
  return raw === "host" ? "host" : "guest";
}

/**
 * Like `parseStoredMode`, but preserves the distinction between an explicit
 * stored choice and "no saved preference". Only the literal strings `"host"`
 * and `"guest"` count as explicit choices; anything else (missing key,
 * corrupted value, wrong type) is `null` — no preference recorded.
 */
export function parseStoredModePreference(raw: unknown): AppMode | null {
  if (raw === "host") return "host";
  if (raw === "guest") return "guest";
  return null;
}

/**
 * Resolves the mode to open in on login from the raw stored value and the
 * user's host eligibility.
 *
 * - Non-hosts are always pinned to `"guest"`.
 * - A host with an explicit saved choice (`"host"` or `"guest"`) keeps it.
 * - A host with NO saved preference defaults to `"host"` — hosts should land
 *   on their host tools on a fresh browser/device instead of renter view.
 */
export function resolveInitialMode(
  raw: unknown,
  profile: HostEligibilityProfile | null | undefined,
): AppMode {
  if (!canEnterHostMode(profile)) return "guest";
  return parseStoredModePreference(raw) ?? "host";
}

/**
 * Routes that belong to the **guest** sidebar. When a host with `mode='host'`
 * lands here, the chrome should flip to `'guest'` so the surrounding UI
 * matches the page.
 *
 * Only routes whose URL is exclusive to the guest hat go here. Shared
 * surfaces such as `/messages` and `/settings` (which exist for both hosts
 * and guests at the same URL) MUST NOT be classified — otherwise route-sync
 * would silently flip a host into guest mode whenever they open Messages or
 * Account Settings, violating the rule that the arrow toggle is the only
 * in-app mode switcher.
 */
export function isGuestRoute(pathname: string): boolean {
  return (
    pathname === "/trips" ||
    pathname.startsWith("/trips/") ||
    pathname === "/favourites" ||
    pathname.startsWith("/favourites/") ||
    pathname === "/profile" ||
    pathname === "/notifications" ||
    pathname.startsWith("/notifications/")
  );
}

/**
 * Routes that belong to the **host** sidebar. /dashboard/* covers all of
 * Calendar, Bookings, Vehicles, Earnings, Reviews, Notifications, Profile.
 */
export function isHostRoute(pathname: string): boolean {
  return pathname === "/dashboard" || pathname.startsWith("/dashboard/");
}

/**
 * Map a current `AppMode` to the navigation/tab bar key that the surface
 * should render. Mobile uses these as bottom-tab identifiers; web uses
 * them as the sidebar variant.
 */
export type NavKey = "guest-nav" | "host-nav";

export function navKeyForMode(mode: AppMode): NavKey {
  return mode === "host" ? "host-nav" : "guest-nav";
}

/**
 * Classification result for a pathname — used by route-sync layers on both
 * web and mobile to decide whether the chrome needs to flip modes.
 *
 *  - `"host"`    → switch to host mode (host-only route)
 *  - `"guest"`   → switch to guest mode (guest-only route)
 *  - `"shared"`  → leave the current mode alone (shared surface)
 */
export type RouteAffinity = "host" | "guest" | "shared";

export function classifyRoute(pathname: string): RouteAffinity {
  if (isHostRoute(pathname)) return "host";
  if (isGuestRoute(pathname)) return "guest";
  return "shared";
}
